import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { apiService } from '../../services/api';
import Header from '../../components/customer/Header';
import WhatsAppButton from '../../components/customer/WhatsAppButton';
import SEO from '../../components/common/SEO';
import styles from './ProductDetail.module.css';
import CheckoutModal from '../../components/customer/CheckoutModal';

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart, cartItems, updateQuantity, removeFromCart, fetchCartItems } = useCart();
  const [quantity, setQuantity] = useState(0);
  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [mainImage, setMainImage] = useState('');
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [attributes, setAttributes] = useState([]); // Todos los atributos disponibles
  const [selectedVariants, setSelectedVariants] = useState({}); // Variantes seleccionadas por el usuario
  const [productVariants, setProductVariants] = useState([]); // Todas las variantes del producto

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      const data = await apiService.getProductBySlug(slug);
      setProduct(data);
      console.log('Datos de la pantalla detail',data)
      setLoading(false);
    };
    fetchProduct();
  }, [slug]);

  // Cargar atributos específicos del producto
  useEffect(() => {
    const loadAttributes = async () => {
      if (!product?.id) return;
      
      try {
        // Obtener atributos específicos del producto
        const response = await apiService.getProductAttributes(product.id);
        console.log('Atributos del producto:', response);
        
        // Cargar opciones para cada atributo variante
        const attributesWithOptions = await Promise.all(
          (response.attributes || []).map(async (attr) => {
            if (attr.category === 'variant') {
              const optionsResponse = await apiService.getAttributeOptions(attr.id);
              return {
                ...attr,
                attribute_options: optionsResponse.options || []
              };
            }
            return attr;
          })
        );
        console.log('Atributos con opciones:', attributesWithOptions);
        setAttributes(attributesWithOptions);
        
        // Cargar variantes del producto
        const variantsResponse = await apiService.request(`/products/${product.id}/variants`);
        console.log('Variantes del producto:', variantsResponse.variants);
        setProductVariants(variantsResponse.variants || []);
      } catch (err) {
        console.error('Error cargando atributos/variantes del producto:', err);
      }
    };
    loadAttributes();
  }, [product?.id]);

  useEffect(() => {
    const savedEmail = localStorage.getItem('guest_email');
    if (savedEmail) setEmail(savedEmail);
  }, []);

  useEffect(() => {
    if (product && cartItems) {
      const cartItem = cartItems.find(item => item.id === product.id);
      setQuantity(cartItem ? cartItem.quantity : 0);
    }
  }, [cartItems, product]);

  // Función para resolver variant_id a partir de la selección de atributos
  const resolveVariantId = (selection) => {
    if (!productVariants || productVariants.length === 0) return null;
    
    // Filtrar variantes que coincidan con la selección
    const matchingVariant = productVariants.find(variant => {
      if (!variant.product_variant_attributes) return false;
      
      // Verificar que todos los atributos de la variante coincidan con la selección
      const matches = variant.product_variant_attributes.every(va => {
        const attrName = va.product_attributes?.name;
        const optionValue = va.attribute_options?.value;
        
        // Buscar si este atributo está en la selección
        return selection[attrName] === optionValue;
      });
      
      // Verificar que la selección tenga la cantidad de atributos correcta
      return matches && Object.keys(selection).length === (variant.product_variant_attributes?.length || 0);
    });
    
    return matchingVariant?.id || null;
  };

  const handleAddToCart = async () => {
    if (!email) {
      setShowEmailInput(true);
      return;
    }
    localStorage.setItem('guest_email', email);
    
    // Resolver variant_id a partir de la selección
    const variantId = resolveVariantId(selectedVariants);
    console.log('Resolved variant_id:', variantId, 'from selection:', selectedVariants);
    
    // Crear producto con variant_id incluido
    const productWithVariant = {
      ...product,
      variant_id: variantId
    };
    
    await addToCart(productWithVariant, 1, email, selectedVariants);
    await fetchCartItems();
    setShowEmailInput(false);
  };

  const handleIncrease = async () => {
    await updateQuantity(product.id, quantity + 1, email);
    await fetchCartItems();
  };

  const handleDecrease = async () => {
    if (quantity > 1) {
      await updateQuantity(product.id, quantity - 1, email);
      await fetchCartItems();
    } else {
      await removeFromCart(product.id, email);
      await fetchCartItems();
    }
  };

  const handleBuyNow = () => {
    setShowCheckoutModal(true);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className={styles.loading}>Cargando...</div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Header />
        <div className={styles.notFound}>Producto no encontrado</div>
      </>
    );
  }

  const primaryImageUrl = mainImage || product.product_images?.find(img => img.is_primary)?.cloudflare_url
    || product.product_images?.[0]?.cloudflare_url
    || '/placeholder-image.jpg';

  return (
    <>
      <SEO 
        title={`${product.name} - Nexa Global`}
        description={product.description?.substring(0, 160) || `Compra ${product.name} en Nexa Global. Envío a toda Colombia.`}
        canonical={`${window.location.origin}/product/${product.slug}`}
      />
      <Header />
      <div className={styles.amazonContainer}>
        <div className={styles.leftColumn}>
          <div className={styles.imageGallery}>
            <img src={primaryImageUrl} alt={product.name} className={styles.mainImage} />
            <div className={styles.thumbnailList}>
              {product.product_images?.map((img, idx) => (
                <img
                  key={img.id || idx}
                  src={img.cloudflare_url}
                  alt={img.alt_text || product.name}
                  className={styles.thumbnail}
                  onClick={() => setMainImage(img.cloudflare_url)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.detailsColumn}>
          <h1 className={styles.name}>{product.name}</h1>
          
          <div className={styles.priceSection}>
            <span className={styles.price}>${product.price.toFixed(2)}</span>
            {product.original_price && (
              <span className={styles.originalPrice}>${product.original_price.toFixed(2)}</span>
            )}
          </div>
          {product.is_prime && (
            <div className={styles.badges}>
              <span className={styles.prime}>Prime</span>
            </div>
          )}
          <div className={styles.description}>
            <h2>Sobre este producto</h2>
            <p>{product.description}</p>
          </div>

          {/* Atributos Variables (Opciones de selección) */}
          {attributes.filter(a => a.category === 'variant').length > 0 && (
            <div className={styles.variantSelectors}>
              <h3>Opciones disponibles</h3>
              {attributes.filter(a => a.category === 'variant').map(attr => (
                <div key={attr.id} className={styles.variantGroup}>
                  <label className={styles.variantLabel}>{attr.name}:</label>
                  <div className={styles.variantOptions}>
                    {attr.type === 'color' ? (
                      // Color picker con paleta
                      <div className={styles.colorPalette}>
                        {attributes.find(a => a.id === attr.id)?.attribute_options?.map(option => (
                          <button
                            key={option.id}
                            className={`${styles.colorOption} ${selectedVariants[attr.name] === option.value ? styles.selected : ''}`}
                            style={{
                              backgroundColor: option.color_hex || '#ccc',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              border: selectedVariants[attr.name] === option.value ? '3px solid #333' : '2px solid #ddd',
                              cursor: 'pointer',
                              title: option.display_name
                            }}
                            onClick={() => setSelectedVariants({...selectedVariants, [attr.name]: option.value})}
                            title={option.display_name}
                          />
                        ))}
                      </div>
                    ) : (
                      // Selector de opciones de texto/talla
                      <select
                        value={selectedVariants[attr.name] || ''}
                        onChange={(e) => setSelectedVariants({...selectedVariants, [attr.name]: e.target.value})}
                        className={styles.variantSelect}
                      >
                        <option value="">Selecciona {attr.name.toLowerCase()}</option>
                        {attributes.find(a => a.id === attr.id)?.attribute_options?.map(option => (
                          <option key={option.id} value={option.value}>
                            {option.display_name || option.value}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Atributos Informativos (Especificaciones) */}
          {attributes.filter(a => a.category === 'specification').length > 0 && (
            <div className={styles.detailsTable}>
              <h3>Características</h3>
              {attributes.filter(a => a.category === 'specification').map(attr => (
                <div key={attr.id} className={styles.specRow}>
                  <strong>{attr.name}:</strong> 
                  <span>{attr.description || 'N/A'}</span>
                </div>
              ))}
            </div>
          )}

          {/* Información del producto */}
          <div className={styles.detailsTable}>
            <div><strong>Marca:</strong> {product.brand || 'N/A'}</div>
            
            {product.weight && <div><strong>Peso:</strong> {product.weight} kg</div>}
          </div>
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.buyBox}>
            <div className={styles.priceBuyBox}>${product.price.toFixed(2)}</div>
            <div className={styles.stockBuyBox}>
              {product.stock_quantity > 0 ? 'Disponible' : 'Agotado'}
            </div>
            <div className={styles.cartControls}>
              {showEmailInput ? (
                <div className={styles.emailInputContainer}>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Ingresa tu email"
                    className={styles.emailInput}
                  />
                  <button
                    className={styles.addToCart}
                    onClick={handleAddToCart}
                    disabled={!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
                  >
                    Confirmar
                  </button>
                </div>
              ) : quantity > 0 ? (
                <div className={styles.cartQuantityControls}>
                  <button
                    className={styles.decreaseBtn}
                    onClick={handleDecrease}
                    disabled={quantity === 0}
                  >−</button>
                  <span className={styles.quantity}>{quantity}</span>
                  <button
                    className={styles.increaseBtn}
                    onClick={handleIncrease}
                    disabled={quantity >= product.stock_quantity}
                  >+</button>
                </div>
              ) : (
                <button
                  className={styles.addToCart}
                  onClick={handleAddToCart}
                  disabled={product.stock_quantity === 0}
                >
                  Agregar al carrito
                </button>
              )}
            </div>
            <div className={styles.buyNowBox}>
              <button className={styles.buyNowBtn} onClick={handleBuyNow}>Comprar ahora</button>
            </div>

            <div><strong>🔔 stock disponible:</strong> {product.stock_quantity}</div>
          </div>
        </div>
      </div>
      {/* Modal de checkout */}
      <CheckoutModal
  open={showCheckoutModal}
  onClose={() => setShowCheckoutModal(false)}
  directProduct={{ ...product, quantity: 1, variantesSeleccionadas: selectedVariants }}
      />
      <WhatsAppButton />
    </>
  );
};

export default ProductDetail;